import {
  ItemTypeConsts, StoreModuleMap,
} from 'client/util/Constants';
import { events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { get } from 'lodash';
import MspState from 'components/models/MspState';
import OptimizelyApi, { experiments } from 'client/GrowthEngineering/OptimizelyApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import hashString from 'client/GrowthEngineering/utilities/hashString';

const growthModule = StoreModuleMap[ItemTypeConsts.growth];
let store;

const trackEvent = (event, props = {}) => {
  const { isMobile } = store.state[growthModule];

  // include user device information
  const userDevice = {
    isMobile,
    screenSize: window.screen.width,
  };

  // add provider ID if MSP or Child
  const providerId = MspState.isAnMspOrChild
    ? hashString(MspState.providerId)
    : undefined;

  // Fire tracking event
  window.analytics.track(event, {
    ...props,
    // add org ID if event triggered outside of MTP
    ...(!MspState.displayMtpView && { orgId: hashString(MspState.orgId) }),
    providerId,
    userDevice,
  });
};

/**
 * Triggers a Segment event if the existing variation for a particular experiment in
 * the orgGrowthData object and the variation returned from Optimizely do not match
 * @param {Object} growthExperiment - The experiment from the orgGrowthData object
 * @param {String} variation - The variation name returned from OptimizelyApi.getVariation
 */
const checkOrgGrowthDataExperimentVariation = (growthExperiment, variation) => {
  const growthVariation = get(growthExperiment, 'variationState.variationName', '');
  const experimentMismatch = growthVariation !== variation;

  if (growthVariation && variation && experimentMismatch) {
    const experiment = {
      experimentName: growthExperiment.experimentName,
      growthDataVariation: growthVariation,
      optimizelyVariation: variation,
    };

    // Fire tracking event
    trackEvent(events.experimentVariationMismatch, experiment);
  }
};

/**
 * Trigger an event through Segment. This is propagated to Optimizely through Segment.
 * @param {String} event - The name of the event to be triggered
 * @param {Object} props - Properties to send along with the event
 */
const triggerEvent = (event, props = {}) => {
  if (!window || !window.analytics) return;

  // Append the user's current Optimizely variation for each experiment
  const optimizelyVariations = {};
  if (window.optimizely && window.optimizely.get) {
    const state = window.optimizely.get('state');
    Object.keys(experiments).forEach((experimentKey) => {
      const experiment = experiments[experimentKey];
      const variation = OptimizelyApi.getVariation(experiment, state);
      optimizelyVariations[experimentKey] = variation;

      const { orgGrowthData } = store.state[growthModule];
      // Check if experiment is already in org Growth data object, if so check variation
      if (orgGrowthData.experimentStates
        && orgGrowthData.experimentStates[experiment.experimentId]) {
        checkOrgGrowthDataExperimentVariation(
          orgGrowthData.experimentStates[experiment.experimentId], variation,
        );
      }
    });
  }

  const eventProps = {
    ...props,
    optimizelyVariations,
  };

  // Fire tracking event
  trackEvent(event, eventProps);
};

/**
 * Trigger aside panel visited events
 */

const triggerLdapAsidePanelVisited = (payload = {}) => {
  triggerEvent(events.ldapAsidePanelVisited, payload);
};

const triggerSoftwareAppsAsidePanelVisited = (payload = {}) => {
  triggerEvent(events.softwareManagementAsidePanelVisited, payload);
};

/**
 * Trigger Item addition events
 */

const triggerSystemAdded = (resourceName = '') => {
  triggerEvent(events.systemAdded, { resourceName });
  triggerEvent(events.resourceAdded, {
    resourceType: 'system',
    resourceName,
  });
};

/**
 * Trigger MTP Admin Events
 */
const triggerAdminConsoleLaunched = (payload = {}) => {
  triggerEvent(events.adminConsoleLaunched, payload);
};
const triggerOrgDetailExpanded = (payload = {}) => {
  triggerEvent(events.orgDetailExpanded, payload);
};
const triggerAddOrgModalOpened = (payload = {}) => {
  triggerEvent(events.addOrgModalOpened, payload);
};
const triggerNewOrgAdded = (payload = {}) => {
  triggerEvent(events.newOrgAdded, payload);
};

/**
 * Trigger Onboarding Events
 */
const triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked = () => {
  triggerEvent(events.onboardingChecklistModalAddSystemsInBulkLinkClicked);
};

const triggerSystemSetupModalDismissed = () => {
  triggerEvent(events.systemSetupModalDismissed);
};

const triggerSystemSetupModalViewed = () => {
  triggerEvent(events.systemSetupModalViewed);
};

/**
 * Trigger Payment events
 */
const triggerPaymentModalViewed = async (props = {}) => {
  const totalCount = await SystemUserApi.fetchTotalCount();
  triggerEvent(events.paymentModalViewed, {
    numUsers: totalCount,
    ...props,
  });
};

const triggerPaymentModalDismissed = async (props = {}) => {
  const totalCount = await SystemUserApi.fetchTotalCount();
  triggerEvent(events.paymentModalDismissed, {
    numUsers: totalCount,
    ...props,
  });
};

/**
 * Trigger System Insights events
 */
const triggerSystemInsightsTableSelected = (payload = {}) => {
  triggerEvent(events.systemInsightsTableSelected, payload);
};

const triggerSystemInsightsExportClicked = (payload = {}) => {
  triggerEvent(events.systemInsightsExportClicked, payload);
};

export default {
  checkOrgGrowthDataExperimentVariation,
  triggerAddOrgModalOpened,
  triggerAdminConsoleLaunched,
  triggerEvent,
  triggerLdapAsidePanelVisited,
  triggerNewOrgAdded,
  triggerOnboardingChecklistModalAddSystemsInBulkLinkClicked,
  triggerOrgDetailExpanded,
  triggerPaymentModalDismissed,
  triggerPaymentModalViewed,
  triggerSoftwareAppsAsidePanelVisited,
  triggerSystemAdded,
  triggerSystemInsightsExportClicked,
  triggerSystemInsightsTableSelected,
  triggerSystemSetupModalDismissed,
  triggerSystemSetupModalViewed,

  // For the shortterm, we still need to set the store for this object
  // This should be moved into ui-events as the epic (OX-1413) goes along
  setStore(_store) {
    store = _store;
  },

  /**
   * Trigger events on route changes based on the URL the admin visits.
   *
   * NOTE: don't add more views/events to this implementation
   * "page viewed" event is configured to track all routes
   *
   * @param {String} path - The URL path (e.g. "users")
   */
  handlePageVisited(path) {
    const pathToEventMap = {
      applications: [events.applicationsPageVisited],
      commands: [events.commandsPageVisited],
      devices: [events.systemsPageVisited],
      directories: [events.directoriesPageVisited],
      groups: [events.groupsPageVisited],
      ldap: [events.ldapPageVisited],
      home: [events.insightsHomePageVisited],
      insights: [events.insightsPageVisited],
      mdm: [events.mdmPageVisited],
      notifications: [events.notificationsPageVisited],
      policies: [events.policiesPageVisited],
      radius: [events.radiusPageVisited],
      reports: [events.reportsPageVisited],
      settings: [events.settingsPageVisited],
      support: [events.supportPageVisited],
      users: [events.usersPageVisited],
    };

    const eventsToTrigger = pathToEventMap[path];
    if (eventsToTrigger) {
      eventsToTrigger.forEach(event => triggerEvent(event));
    }
  },
};
